import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Package, Mail, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo: "/logo.png",
};

export default function ShopSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/shop/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setOrder(response.data.order);
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 10) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
          setOrder(response.data.order);
        }
      } catch (error) {
        console.error("Error checking status:", error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex items-center gap-6">
          <Link to="/">
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-24 text-center">
        {status === "loading" && (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 text-white mx-auto animate-spin" />
            <h1 className="heading-lg text-2xl text-white">Processing your order...</h1>
            <p className="text-white/60">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "success" && order && (
          <div className="space-y-8">
            <div className="w-20 h-20 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            
            <div>
              <h1 className="heading-xl text-3xl text-white mb-4">Order Confirmed!</h1>
              <p className="text-white/60">
                Thank you for your pre-order. Your pair of The Gaffer is secured.
              </p>
            </div>

            <div className="border border-white/10 p-6 bg-white/[0.02] text-left">
              <h2 className="heading-md text-lg text-white mb-4">Order Details</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Order Number</span>
                  <span className="text-white font-mono">{order.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Product</span>
                  <span className="text-white">{order.product_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Size</span>
                  <span className="text-white">{order.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Total Paid</span>
                  <span className="text-white">{order.total_in_currency} {order.currency}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="border border-white/10 p-4 text-center">
                <Mail className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white/60 text-sm">Confirmation email sent to</p>
                <p className="text-white text-sm">{order.customer_email}</p>
              </div>
              <div className="border border-white/10 p-4 text-center">
                <Package className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white/60 text-sm">Expected Delivery</p>
                <p className="text-white text-sm">September 2026</p>
              </div>
            </div>

            <Link to="/" className="btn-secondary inline-block">
              Return to Homepage
            </Link>
          </div>
        )}

        {status === "pending" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-yellow-500/20 border border-yellow-500/40 rounded-full flex items-center justify-center mx-auto">
              <Package className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="heading-lg text-2xl text-white">Payment Processing</h1>
            <p className="text-white/60">
              Your payment is being processed. You'll receive a confirmation email shortly.
              If you don't receive it within 10 minutes, please contact us.
            </p>
            <Link to="/" className="btn-secondary inline-block">
              Return to Homepage
            </Link>
          </div>
        )}

        {status === "expired" && (
          <div className="space-y-6">
            <h1 className="heading-lg text-2xl text-white">Session Expired</h1>
            <p className="text-white/60">
              Your checkout session has expired. Please try again.
            </p>
            <Link to="/shop" className="btn-primary inline-block">
              Return to Shop
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <h1 className="heading-lg text-2xl text-white">Something Went Wrong</h1>
            <p className="text-white/60">
              We couldn't verify your payment. If you were charged, please contact us with your details.
            </p>
            <Link to="/shop" className="btn-primary inline-block">
              Return to Shop
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
